<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Titre de la gamme -->
    <div>
      <label
        for="edit-titre"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        Nom de la gamme *
      </label>
      <input
        id="edit-titre"
        v-model="formData.titre"
        type="text"
        required
        placeholder="Ex: T-shirts Homme"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        :class="{ 'border-red-500': errors.titre }"
      />
      <p v-if="errors.titre" class="mt-1 text-sm text-red-600">
        {{ errors.titre }}
      </p>
    </div>

    <!-- Section des tailles -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700">
          Tailles disponibles
        </label>
        <div class="text-sm text-gray-500">Colonnes d1 à d12</div>
      </div>

      <div class="grid grid-cols-6 gap-3">
        <div
          v-for="(size, index) in sizeColumns"
          :key="index"
          class="text-center"
        >
          <div
            class="px-2 py-1 rounded-md text-sm font-medium border"
            :class="
              size ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
            "
          >
            <input
              v-model="sizeColumns[index]"
              type="text"
              placeholder="-"
              class="w-full text-center bg-transparent border-none outline-none text-sm"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
      <button
        type="button"
        @click="$emit('cancel')"
        class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Annuler
      </button>
      <button
        type="submit"
        :disabled="isSubmitting"
        class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
        Sauvegarder les modifications
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from "vue";
import type { GammeComplete, UpdateGammeData } from "@/types";

interface Props {
  gamme: GammeComplete;
}

interface Emits {
  (e: "submit", data: UpdateGammeData): void;
  (e: "cancel"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isSubmitting = ref(false);

const formData = reactive<UpdateGammeData>({
  id_declinaison: 0,
  titre: "",
  d1_FR: null,
  d2_FR: null,
  d3_FR: null,
  d4_FR: null,
  d5_FR: null,
  d6_FR: null,
  d7_FR: null,
  d8_FR: null,
  d9_FR: null,
  d10_FR: null,
  d11_FR: null,
  d12_FR: null,
});

const errors = reactive({
  titre: "",
});

// Colonnes de tailles réactives
const sizeColumns = ref<(string | null)[]>([
  formData.d1_FR,
  formData.d2_FR,
  formData.d3_FR,
  formData.d4_FR,
  formData.d5_FR,
  formData.d6_FR,
  formData.d7_FR,
  formData.d8_FR,
  formData.d9_FR,
  formData.d10_FR,
  formData.d11_FR,
  formData.d12_FR,
]);

// Initialiser le formulaire
watch(
  () => props.gamme,
  (newGamme) => {
    if (newGamme) {
      formData.id_declinaison = parseInt(newGamme.id_declinaison.toString());
      formData.titre = newGamme.titre || "";

      // Mettre à jour les colonnes de tailles
      sizeColumns.value = [
        newGamme.d1_FR,
        newGamme.d2_FR,
        newGamme.d3_FR,
        newGamme.d4_FR,
        newGamme.d5_FR,
        newGamme.d6_FR,
        newGamme.d7_FR,
        newGamme.d8_FR,
        newGamme.d9_FR,
        newGamme.d10_FR,
        newGamme.d11_FR,
        newGamme.d12_FR,
      ];
    }
  },
  { immediate: true },
);

// Synchroniser les colonnes avec formData
watch(
  sizeColumns,
  (newColumns) => {
    formData.d1_FR = newColumns[0] || null;
    formData.d2_FR = newColumns[1] || null;
    formData.d3_FR = newColumns[2] || null;
    formData.d4_FR = newColumns[3] || null;
    formData.d5_FR = newColumns[4] || null;
    formData.d6_FR = newColumns[5] || null;
    formData.d7_FR = newColumns[6] || null;
    formData.d8_FR = newColumns[7] || null;
    formData.d9_FR = newColumns[8] || null;
    formData.d10_FR = newColumns[9] || null;
    formData.d11_FR = newColumns[10] || null;
    formData.d12_FR = newColumns[11] || null;
  },
  { deep: true },
);

const validateForm = () => {
  errors.titre = "";

  if (!formData.titre.trim()) {
    errors.titre = "Le nom de la gamme est requis";
    return false;
  }

  return true;
};

const hasAtLeastOneSize = (data: UpdateGammeData) => {
  const sizes = [
    data.d1_FR,
    data.d2_FR,
    data.d3_FR,
    data.d4_FR,
    data.d5_FR,
    data.d6_FR,
    data.d7_FR,
    data.d8_FR,
    data.d9_FR,
    data.d10_FR,
    data.d11_FR,
    data.d12_FR,
  ];
  return sizes.some(
    (s) => s != null && s !== undefined && String(s).trim() !== "",
  );
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  isSubmitting.value = true;

  try {
    let payload: UpdateGammeData = { ...formData };
    if (!hasAtLeastOneSize(payload) && props.gamme) {
      payload = {
        ...payload,
        d1_FR: props.gamme.d1_FR ?? null,
        d2_FR: props.gamme.d2_FR ?? null,
        d3_FR: props.gamme.d3_FR ?? null,
        d4_FR: props.gamme.d4_FR ?? null,
        d5_FR: props.gamme.d5_FR ?? null,
        d6_FR: props.gamme.d6_FR ?? null,
        d7_FR: props.gamme.d7_FR ?? null,
        d8_FR: props.gamme.d8_FR ?? null,
        d9_FR: props.gamme.d9_FR ?? null,
        d10_FR: props.gamme.d10_FR ?? null,
        d11_FR: props.gamme.d11_FR ?? null,
        d12_FR: props.gamme.d12_FR ?? null,
      };
    }
    emit("submit", payload);
  } catch (error) {
    console.error("Erreur lors de la modification:", error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
